import { useCalculatorDescriptors } from '@e2grnd/ui-core'
import { ModuleFallback } from '@e2grnd/ui-core/dist/components/loaders/ModuleFallback'
import { useAuthContext } from '@e2grnd/ui-core/dist/contexts/AuthProvider'
import type { TCalculatorContext } from '@e2grnd/ui-core/dist/contexts/CalculatorContext'
import CalculatorProvider from '@e2grnd/ui-core/dist/contexts/CalculatorProvider'
import FreeformWorkflowRouter from '@e2grnd/ui-core/dist/routes/FreeformWorkflowRouter'
import WorkflowRouter from '@e2grnd/ui-core/dist/routes/WorkflowRouter'
import type { CalculatorExports } from '@e2grnd/ui-core/dist/types'
import NoSsr from '@mui/material/NoSsr'
import Debug from 'debug'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

// Import calculator configs and metas using Vite's glob import
// These create lazy-loadable modules for each calculator
const calculatorConfigs = import.meta.glob('../calculators/*/index.{ts,tsx,js,jsx}')
// Meta is generated as directories with index.ts files (containing executable JS modules for conditionals/components)
const calculatorMetas = import.meta.glob('../meta/calculators/*/index.ts')

const log = Debug('e2g:DynamicCalculatorPage:debug')
log.log = console.info.bind(console)

function CalculatorNotFound({ calcId, error }: { calcId?: string; error?: string }) {
  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>Calculator Not Found</h2>
      <p>The calculator "{calcId}" could not be loaded.</p>
      {error && <p style={{ color: 'red' }}>{error}</p>}
    </div>
  )
}

type CalculatorMeta = TCalculatorContext['meta']

type CalculatorLoaderState =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'not-found' }
  | { status: 'loaded'; config: CalculatorExports; meta: CalculatorMeta }

function useCalculatorLoader(calcId: string | undefined) {
  const [state, setState] = useState<CalculatorLoaderState>({ status: 'loading' })

  useEffect(() => {
    if (!calcId) {
      setState({ status: 'not-found' })
      return
    }

    const configPath = Object.keys(calculatorConfigs).find(path => path.includes(`/${calcId}/index.`))
    const metaPath = Object.keys(calculatorMetas).find(path => path.includes(`/${calcId}/index.ts`))

    if (!configPath || !metaPath) {
      setState({ status: 'not-found' })
      return
    }

    let cancelled = false
    setState({ status: 'loading' })

    async function loadCalculator() {
      try {
        // biome-ignore lint/style/noNonNullAssertion: configPath is guaranteed set when this runs
        const configModule = (await calculatorConfigs[configPath!]()) as {
          config?: CalculatorExports
          default?: CalculatorExports
        }
        const config = configModule.config ?? configModule.default
        if (!config) {
          throw new Error(`Calculator "${calcId}" module did not export a config or default`)
        }

        let meta: CalculatorMeta = {}
        if (metaPath) {
          const metaModule = (await calculatorMetas[metaPath]()) as { default?: CalculatorMeta } & CalculatorMeta
          meta = metaModule.default ?? metaModule
        }

        if (!cancelled) {
          setState({ status: 'loaded', config, meta })
        }
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'error', error: String(err) })
        }
      }
    }

    loadCalculator()

    return () => {
      cancelled = true
    }
  }, [calcId])

  return state
}

function CalculatorPageInner({
  calcId,
  config,
  meta,
}: {
  calcId: string
  config: CalculatorExports
  meta: CalculatorMeta
}) {
  const { accessControl } = useAuthContext()
  const { calculatorDescriptors } = useCalculatorDescriptors()
  const descriptor = calculatorDescriptors[calcId]

  if (!descriptor) {
    return <CalculatorNotFound calcId={calcId} error="Descriptor not found in context" />
  }

  log(`CalculatorPage "${calcId}" loaded`)

  const isFreeform = descriptor.freeform === true

  return (
    <CalculatorProvider accessControl={accessControl} config={config} descriptor={descriptor} meta={meta}>
      {isFreeform ? <FreeformWorkflowRouter /> : <WorkflowRouter />}
    </CalculatorProvider>
  )
}

/**
 * Dynamic Calculator Page Component
 * Handles dynamic loading of calculator modules based on the calcId route parameter.
 */
export function DynamicCalculatorPage() {
  const { calcId } = useParams<{ calcId: string }>()
  const loaderState = useCalculatorLoader(calcId)

  let content: React.ReactNode
  switch (loaderState.status) {
    case 'loading':
      content = <ModuleFallback />
      break
    case 'not-found':
      content = <CalculatorNotFound calcId={calcId} error={calcId ? 'Calculator not available' : undefined} />
      break
    case 'error':
      content = <CalculatorNotFound calcId={calcId} error={loaderState.error} />
      break
    case 'loaded':
      // biome-ignore lint/style/noNonNullAssertion: calcId is guaranteed set in 'loaded' state
      content = <CalculatorPageInner calcId={calcId!} config={loaderState.config} meta={loaderState.meta} />
      break
  }

  return <NoSsr>{content}</NoSsr>
}
